import React, { useState, useEffect } from 'react';
import {
    Star, MessageCircle, Users, Search,
    ChevronRight, ChevronDown, User, Bell, Heart, Share2,
    Shield, Verified, MapPin, Calendar, Clock, DollarSign,
    Mail, Instagram, Globe, Bookmark,
    CheckCircle, Info,
    Target, Trophy, Crown,
    PlusCircle, Edit3, Camera,
    BarChart3, Activity, X, LogOut,
    Loader, AlertTriangle, Copy
} from 'lucide-react';
import { API_BASE_URL } from '../constants';
import { serviceTypes, categories, regions, reviewTypes, mockServices } from '../constants';

import { EnhancedDropdown } from './EnhancedDropdown';
import ProfileImage from './ProfileImage';

function formatFollowers(num) {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return num;
}


function HomePage({
    isLoggedIn,
    isVerified,
    userReviews,
    setShowQuickReview,
    activeFilters,
    searchQuery,
    filteredServices,
    setActiveFilters,
    setSearchQuery,
    bookmarkedServices,
    setBookmarkedServices,
    followedProviders,
    setFollowedProviders,
    handleServiceBookmark,
    handleProviderFollow,
    openServiceProfile,
    startReviewFlow, // Add this prop
    // -----
    // businesses,
}) {
    const [imgError, setImgError] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [searchResult, setSearchResult] = useState([]);
    const [businesses, setBusinesses] = useState({});
    const [viewMode, setViewMode] = useState('grid'); // grid, list, map
    const [userReviewsToShow, setUserReviewsToShow] = React.useState(6);
    const [showMyProfileModal, setShowMyProfileModal] = useState(false);
    const [selectedServiceForReview, setSelectedServiceForReview] = useState(null);
    // State for top-rated reviews by category
    const [topCategoryReviews, setTopCategoryReviews] = useState({});

    const uniqueCategories = Array.from(new Set(serviceTypes.map(st => st.category)));

    // startReviewFlow is now passed as a prop from App.js

    // Save user data when it changes
    useEffect(() => {
        localStorage.setItem('bookmarkedServices', JSON.stringify(bookmarkedServices));
    }, [bookmarkedServices]);

    useEffect(() => {
        localStorage.setItem('followedProviders', JSON.stringify(followedProviders));
    }, [followedProviders]);

    // Load user data on component mount
    useEffect(() => {
        const savedBookmarks = localStorage.getItem('bookmarkedServices');
        const savedFollows = localStorage.getItem('followedProviders');

        if (savedBookmarks) {
            setBookmarkedServices(JSON.parse(savedBookmarks));
        }
        if (savedFollows) {
            setFollowedProviders(JSON.parse(savedFollows));
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        async function fetchAllTopCategoryReviews() {
            // Get unique categories from serviceTypes, excluding 'all'
            const uniqueCategories = Array.from(new Set(serviceTypes.map(st => st.category)));
            // const uniqueCategories = Array.from(new Set(serviceTypes.map(st => st.category))).filter(cat => cat !== 'all');
            // Convert to comma-separated string for API call
            const categoriesString = uniqueCategories.join(',');
            try {
                const reviews = await fetchReviewsByCategory(categoriesString);
                // console.log(reviews);
                // You can process reviews here, e.g. group by category if needed
                if (isMounted) {
                    // Example: set all reviews to state
                    setTopCategoryReviews(reviews);
                }
            } catch (error) {
                console.error(error)
                if (isMounted) setTopCategoryReviews([]);
            }
        }
        fetchAllTopCategoryReviews();
        return () => { isMounted = false; };
    }, []);


    // const [businesses, setBusinesses] = useState({});


    useEffect(() => {
        let isMounted = true;
        async function loadBusinesses() {
            const businesses = await fetchAllBusinesses();
            if (isMounted && businesses) {
                setBusinesses(businesses); // <-- update state here if needed
            }
        }
        loadBusinesses();
        return () => { isMounted = false; };
    }, []);

    const [topBusinesses, setTopBusinesses] = useState({});
    useEffect(() => {
        let isMounted = true;
        async function loadTopBusinesses() {
            const businesses = await fetchTopBusinesses();
            if (isMounted && businesses) {
                setTopBusinesses(businesses);
            }
        }
        loadTopBusinesses();
        return () => { isMounted = false; };
    }, []);

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (value.length > 2 && businesses.length > 0) {
            const suggestions = businesses
                .filter(s =>
                    s?.searchData?.username?.toLowerCase().includes(value.toLowerCase()) ||
                    s?.searchData?.full_name?.toLowerCase().includes(value.toLowerCase()) ||
                    s?.specialties?.some(spec => spec.toLowerCase().includes(value.toLowerCase()))
                )
                .slice(0, 5)
                .map(s => ({
                    type: 'service',
                    text: s?.searchData?.full_name,
                    username: s?.searchData?.username,
                    data: s
                }));

            console.log('Suggestions:', suggestions);
            setSearchSuggestions(suggestions);
        } else {
            setSearchSuggestions([]);
            setSearchResult([]);
        }
    };

    const fetchAllBusinesses = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/business-crud?all=true&limit=10`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.found) {
                    // console.log('✅ Found business in database:', data.business.instagramUsername);
                    return data.businesses || [];
                }
            }
            return null;
        } catch (error) {
            console.error('❌ Error searching business in database:', error);
            return null;
        }
    };

    const fetchTopBusinesses = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/business-crud?top_rated=true&top_reviewed=true&limit=10`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.found) {
                    // console.log('✅ Found business in database:', data.business.instagramUsername);
                    return data.businesses || [];
                }
            }
            return null;
        } catch (error) {
            console.error('❌ Error searching business in database:', error);
            return null;
        }
    };

    const fetchReviewsByCategory = async (categories) => {

        const res = await fetch(`${API_BASE_URL}/api/search-by?type=category&categories=${categories}&limit=20&sortOrder=desc`);
        const data = await res.json();
        return data.reviewsByCategory;
    };

    const fetchReviewsByserviceTypes = async (serviceTypes) => {
        console.log('✅ fetchReviewsByserviceTypes start')
        // for (const serviceType of serviceTypes.filter(serv => serv.name !== 'all')) {
        let serviceType = 'Personal Training'
        const res = await fetch(`${API_BASE_URL}/api/search-by?type=serviceTypes&name=${serviceType}&limit=20&sortOrder=desc`);
        const data = await res.json();
        // console.log(`✅ fetchReviewsByserviceTypes Retrieved ${data.fetchReviewsByCategory.length} reviews for ${serviceType}`);
        return data.reviews;
    };


    const ServiceCard = ({ reviews, service, reviewCount, averageRating }) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" >
            <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">

                            <ProfileImage
                                profilePicture={
                                    service.profile_pic_url || service?.profilePicture
                                }
                                profile_pic_base64={service?.profile_pic_base64}
                                altName={`${service.full_name || service.displayName} profile`}
                                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                            />

                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 text-lg">{service.full_name || service.displayName}</h3>
                            <p className="text-purple-600 text-sm font-medium">{service.instagramUsername || service.username}</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        {service.is_verified && (
                            <div className="flex items-center space-x-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                                <Verified size={12} />
                                <span>Verified</span>
                            </div>
                        )}
                        {service.is_business_account && (
                            <div className="flex items-center space-x-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                                <Verified size={12} />
                                <span>Business</span>
                            </div>
                        )}
                        {service.is_professional_account && (
                            <div className="flex items-center space-x-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                                <Verified size={12} />
                                <span>Professional</span>
                            </div>
                        )}
                        {service.featured && (
                            <div className="flex items-center space-x-1 bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs">
                                <Crown size={12} />
                                <span>Featured</span>
                            </div>
                        )}
                    </div>
                </div>

                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{service.biography}</p>

                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                            <Star className="text-yellow-300" size={14} fill="currentColor" />
                            <span className="font-medium text-gray-900">{averageRating.toFixed(1)}</span>
                            <span>({reviewCount})</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <Users size={14} />
                            <span>{formatFollowers(service.followers)}</span>
                        </div>
                    </div>
                    <div className="font-semibold text-purple-600">{service.price || 0}$/Session</div>
                </div>

                {service.reviewTypes && (
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3 bg-gray-50 rounded-lg p-2">
                        <div className="flex items-center space-x-1">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <span>Services: {service.reviewTypes.service?.rating || 0}★ ({service.reviewTypes.service?.count || 0})</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            <span>Ads: {service.reviewTypes.advertisement?.rating || 0}★ ({service.reviewTypes.advertisement?.count || 0})</span>
                        </div>
                    </div>
                )}

                {/* <div className="flex flex-wrap gap-1 mb-4">
          {service.specialties.slice(0, 3).map((specialty, index) => (
            <span
              key={index}
              className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
            >
              {specialty}
            </span>
          ))}
          {service.specialties.length > 3 && (
            <span className="text-gray-500 text-xs">+{service.specialties.length - 3} more</span>
          )}
        </div> */}
            </div>


            <div className="px-6 pb-6">
                <div className="flex space-x-2">
                    <button
                        onClick={() => openServiceProfile(service, reviews)}
                        className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                    >
                        View Profile
                    </button>
                    <button
                        onClick={() => startReviewFlow(service)}
                        className="flex-1 border border-purple-600 text-purple-600 py-2 px-4 rounded-lg hover:bg-purple-50 transition-colors font-medium text-sm"
                    >
                        Write Review
                    </button>
                </div>

                <div className="flex items-center justify-between mt-3">
                    <button
                        onClick={() => handleServiceBookmark(service.id)}
                        className={`flex items-center space-x-1 text-sm transition-colors ${bookmarkedServices.includes(service.id)
                            ? 'text-purple-600'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Bookmark size={14} fill={bookmarkedServices.includes(service.id) ? 'currentColor' : 'none'} />
                        <span>Save</span>
                    </button>

                    <button
                        onClick={() => handleProviderFollow(service.username)}
                        className={`flex items-center space-x-1 text-sm transition-colors ${followedProviders.includes(service.username)
                            ? 'text-purple-600'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Heart size={14} fill={followedProviders.includes(service.username) ? 'currentColor' : 'none'} />
                        <span>{followedProviders.includes(service.username) ? 'Following' : 'Follow'}</span>
                    </button>

                    <button className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 text-sm transition-colors">
                        <Share2 size={14} />
                        <span>Share</span>
                    </button>
                </div>
            </div>
        </div >


    );

    // Service List Item for List View
    const ServiceListItem = ({ reviews, service, reviewCount, averageRating }) => (
        <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                        <ProfileImage
                            profilePicture={
                                service.profile_pic_url || service?.profilePicture
                            }
                            profile_pic_base64={service?.profile_pic_base64}
                            altName={`${service.full_name || service.displayName} profile`}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                        />
                    </div>

                    <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-lg">{service.name}</h3>
                            {service.is_verified && <Verified className="text-blue-500" size={16} />}
                            {service.is_business_account && <Verified className="text-blue-500" size={16} />}
                            {service.is_professional_account && <Verified className="text-blue-500" size={16} />}
                            {service.featured && <Crown className="text-yellow-500" size={16} />}
                        </div>
                        <p className="text-purple-600 text-sm font-medium mb-2">{service.username}</p>
                        <p className="text-gray-600 text-sm mb-2 line-clamp-1">{service.biography}</p>

                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                                <Star className="text-yellow-400" size={14} fill="currentColor" />
                                <span className="font-medium text-gray-900">{averageRating.toFixed(1)}</span>
                                <span>({reviewCount} reviews)</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <Users size={14} />
                                <span>{formatFollowers(service.followers)} followers</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <Clock size={14} />
                                <span>Responds in {service.responseTime}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-4">
                    <div className="text-right">
                        <div className="font-semibold text-purple-600 text-lg">{service.price}</div>
                        <div className="text-sm text-gray-500">per session</div>
                    </div>

                    <div className="flex flex-col space-y-2">
                        <button
                            onClick={() => openServiceProfile(service, reviews)}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                        >
                            View Profile
                        </button>
                        <button
                            onClick={() => startReviewFlow(service)}
                            className="border border-purple-600 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors font-medium text-sm"
                        >
                            Write Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section with Smart Onboarding */}
            <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-8 sm:py-12 lg:py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
                            Discover Verified Instagram
                            <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                                Service Providers
                            </span>
                        </h1>
                        <p className="text-base sm:text-lg lg:text-xl text-purple-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
                            Find authentic reviews from real customers. Connect with trusted influencers and service providers.
                            Make informed decisions with our verified review system.
                        </p>

                        {/* Mobile-first layout: stack vertically on mobile, horizontal on larger screens */}
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 max-w-4xl mx-auto">
                            <button
                                onClick={() => setShowQuickReview(true)}
                                className="w-full sm:w-auto bg-white text-purple-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-2"
                            >
                                <Edit3 size={18} className="sm:w-5 sm:h-5" />
                                <span>Write a Review</span>
                            </button>
                            {/* Search Bar - Responsive width */}
                            <div className="w-full sm:flex-1 sm:max-w-md relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search services, creators..."
                                    value={searchQuery}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-purple-900 text-sm sm:text-base"
                                />
                                {searchSuggestions.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50">
                                        {searchSuggestions.map((suggestion, index) => (
                                            <div
                                                key={index}
                                                className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2"
                                                onClick={() => {
                                                    setSearchQuery(suggestion.username);
                                                    setSearchSuggestions([]);
                                                    setSearchResult(suggestion);
                                                }}
                                            >
                                                <Search size={14} className="text-gray-400" />
                                                <span className="text-gray-400 text-sm text-left">{suggestion.username || suggestion.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Smart Filters Section */}
            <section className="bg-white border-b border-gray-200 py-4 sm:py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
                        {/* Filter Controls */}
                        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
                            <EnhancedDropdown
                                options={categories}
                                value={categories.find(c => c.id === activeFilters.category)}
                                onChange={(category) => setActiveFilters(prev => ({ ...prev, category: category.id }))}
                                placeholder="All Categories"
                                defaultValue={categories[0]}
                                searchable={true}
                                showTooltips={true}
                                className="w-full sm:w-auto sm:min-w-[160px] lg:min-w-[180px]"
                            />

                            <EnhancedDropdown
                                options={regions}
                                value={regions.find(r => r.id === activeFilters.region)}
                                onChange={(region) => setActiveFilters(prev => ({ ...prev, region: region.id }))}
                                placeholder="All Regions"
                                defaultValue={regions[0]}
                                searchable={true}
                                showTooltips={true}
                                className="w-full sm:w-auto sm:min-w-[140px] lg:min-w-[160px]"
                            />

                            <EnhancedDropdown
                                options={reviewTypes}
                                value={reviewTypes.find(t => t.id === activeFilters.reviewType)}
                                onChange={(type) => setActiveFilters(prev => ({ ...prev, reviewType: type.id }))}
                                placeholder="All Reviews"
                                defaultValue={reviewTypes[0]}
                                searchable={true}
                                showTooltips={true}
                                className="w-full sm:w-auto sm:min-w-[120px] lg:min-w-[140px]"
                            />
                        </div>

                        {/* View Controls and Results */}
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                            <span className="text-sm sm:text-base text-gray-600 text-center sm:text-left">
                                {filteredServices.length} services found
                            </span>

                            <div className="flex items-center justify-center sm:justify-start space-x-1 sm:space-x-2 bg-gray-100 rounded-lg p-1">
                                {['grid', 'list'].map(mode => (
                                    <button
                                        key={mode}
                                        onClick={() => setViewMode(mode)}
                                        className={`p-1.5 sm:p-2 rounded transition-colors ${viewMode === mode
                                            ? 'bg-white shadow-sm text-purple-600'
                                            : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        {mode === 'grid' ? <BarChart3 size={14} className="sm:w-4 sm:h-4" /> : <Activity size={14} className="sm:w-4 sm:h-4" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(activeFilters.category !== 'all' || activeFilters.region !== 'all' || activeFilters.reviewType !== 'all' || searchQuery) && (
                        <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2">
                            <span className="text-xs sm:text-sm text-gray-500 font-medium">Active filters:</span>
                            {activeFilters.category !== 'all' && (
                                <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                                    {categories.find(c => c.id === activeFilters.category)?.icon}
                                    {categories.find(c => c.id === activeFilters.category)?.name}
                                    <button onClick={() => setActiveFilters(prev => ({ ...prev, category: 'all' }))}>
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            {activeFilters.region !== 'all' && (
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                                    {regions.find(r => r.id === activeFilters.region)?.icon}
                                    {regions.find(r => r.id === activeFilters.region)?.name}
                                    <button onClick={() => setActiveFilters(prev => ({ ...prev, region: 'all' }))}>
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            {activeFilters.reviewType !== 'all' && (
                                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                                    {reviewTypes.find(t => t.id === activeFilters.reviewType)?.icon}
                                    {reviewTypes.find(t => t.id === activeFilters.reviewType)?.name}
                                    <button onClick={() => setActiveFilters(prev => ({ ...prev, reviewType: 'all' }))}>
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            {searchQuery && (
                                <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                                    Search: "{searchQuery}"
                                    <button onClick={() => setSearchQuery('')}>
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    setActiveFilters({
                                        category: 'all',
                                        region: 'all',
                                        reviewType: 'all',
                                        priceRange: 'all',
                                        rating: 'all',
                                        verified: false,
                                        availability: 'all'
                                    });
                                    setSearchQuery('');
                                }}
                                className="text-gray-500 hover:text-gray-700 text-sm underline ml-2"
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Search result */}
            {((searchSuggestions || searchResult) && searchQuery) && (
                <section className="py-8 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <div className="flex-1 mx-6 relative">
                                <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center">
                                    <Search className="mr-2 text-gray-400" size={20} />
                                    {activeFilters.category !== 'all'
                                        ? `${categories.find(c => c.id === activeFilters.category)?.name} Services`
                                        : 'Search result for Services, Business or Professional'
                                    }
                                </h2>
                            </div>

                            {filteredServices.length > 0 && (
                                <div className="flex items-center space-x-4">
                                    <div className="text-sm text-gray-500">
                                        Sorted by: <span className="font-medium text-gray-900">Highest Rated</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {searchResult && searchSuggestions.length === 0 && searchResult?.data && (
                            viewMode === 'grid' ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <ServiceCard
                                        key={searchResult?.id}
                                        service={searchResult?.data?.searchData}
                                        reviews={searchResult?.data?.reviews}
                                        reviewCount={searchResult?.data?.reviewCount}
                                        averageRating={searchResult?.data?.averageRating}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <ServiceListItem
                                        key={searchResult?.id}
                                        service={searchResult?.data?.searchData}
                                        reviews={searchResult?.data?.reviews}
                                        reviewCount={searchResult?.data?.reviewCount}
                                        averageRating={searchResult?.data?.averageRating}
                                    />
                                </div>
                            )
                        )}


                        {searchSuggestions && searchSuggestions.length > 0 && (
                            viewMode === 'grid' ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                                    {searchSuggestions.map(suggestion => (
                                        <ServiceCard
                                            key={suggestion?.id}
                                            service={suggestion?.data?.searchData}
                                            reviews={suggestion?.data?.reviews}
                                            reviewCount={suggestion?.data?.reviewCount}
                                            averageRating={suggestion?.data?.averageRating}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {searchSuggestions.map(suggestion => (
                                        <ServiceListItem
                                            key={suggestion?.id}
                                            service={suggestion?.data?.searchData}
                                            reviews={suggestion?.data?.reviews}
                                            reviewCount={suggestion?.data?.reviewCount}
                                            averageRating={suggestion?.data?.averageRating}
                                        />
                                    ))}
                                </div>
                            )
                        )}


                        {(filteredServices.length === 0 && !searchQuery) && (
                            <div className="text-center py-16">
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                                <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p>
                                <button
                                    onClick={() => {
                                        setActiveFilters({
                                            category: 'all',
                                            region: 'all',
                                            reviewType: 'all',
                                            priceRange: 'all',
                                            rating: 'all',
                                            verified: false,
                                            availability: 'all'
                                        });
                                        setSearchQuery('');
                                        setSearchSuggestions([]);
                                        setSearchResult([]);
                                    }}
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        )}
                        {(searchQuery) && (
                            <div className="text-center py-16">
                                {/* <div className="text-6xl mb-4">🔍</div> */}
                                {/* <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                                <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p> */}
                                <button
                                    onClick={() => {
                                        setActiveFilters({
                                            category: 'all',
                                            region: 'all',
                                            reviewType: 'all',
                                            priceRange: 'all',
                                            rating: 'all',
                                            verified: false,
                                            availability: 'all'
                                        });
                                        setSearchQuery('');
                                        setSearchSuggestions([]);
                                        setSearchResult([]);
                                    }}
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )}




            {/* Region filter */}
            {/* {(activeFilters.category !== 'all' || activeFilters.region !== 'all' || activeFilters.reviewType !== 'all' || searchQuery) && ( */}
            {activeFilters.region !== 'all' && (
                <section className="py-6 sm:py-8 bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <pre><code>{JSON.stringify(activeFilters.region, null, 2)}</code></pre>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                {activeFilters.region !== 'all'
                                    ? `🏆 Top Rated by ${regions.find(c => c.id === activeFilters.region)?.name}`
                                    : '🏆 Top Rated by Region'
                                }
                            </h2>
                            <p className="text-gray-600 px-4 sm:px-0">Discover the highest-rated service providers in each Region</p>
                        </div>

                        {/* <pre><code>{JSON.stringify(categories.filter(c => c.id === activeFilters.category), null, 2)}</code></pre> */}

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">

                            {/* const reviews = topCategoryReviews[category.id]?.businesses || []; */}

                            <pre><code>{JSON.stringify(topCategoryReviews, null, 2)}</code></pre>
                            {categories.map(category => {
                                // const reviews = topCategoryReviews[category.id]?.businesses.reviews.location.region || [];
                                const reviews = topCategoryReviews[category.id]?.businesses?.reviews?.filter(rev => rev?.location?.region === activeFilters.region) || [];
                                if (reviews.length === 0) return null;
                                return (
                                    <div key={category.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                                                {category.icon}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900">
                                                    <span className="ml-2">{category.name}</span>
                                                    <Star className="text-yellow-400 inline-block mr-1" size={12} fill="currentColor" />
                                                    {(topCategoryReviews[category.id]?.totalAverageRating ?? 0).toFixed(1)}
                                                    {topCategoryReviews[category.id]?.totalReviewCount && (
                                                        <span className="text-xs text-gray-500 ml-1">(📝 {topCategoryReviews[category.id]?.totalReviewCount || 0})</span>
                                                    )}

                                                </h3>
                                                <p className="text-sm text-gray-600">{category.description}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            {reviews.map((review, index) => (
                                                <div
                                                    key={review.id || review._id}
                                                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                                    onClick={() => openServiceProfile(review.targetBusiness, review.reviews)}
                                                >
                                                    <div className="flex items-center justify-center w-6 h-6">
                                                        {index === 0 && <span className="text-yellow-500">🥇</span>}
                                                        {index === 1 && <span className="text-gray-400">🥈</span>}
                                                        {index === 2 && <span className="text-orange-600">🥉</span>}
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <ProfileImage
                                                            profilePicture={
                                                                review.targetBusiness?.profilePicture ||
                                                                review.targetBusiness?.profile_pic_url ||
                                                                review.targetBusiness?.instagramData?.profile_pic_url
                                                            }
                                                            profile_pic_base64={review.targetBusiness?.profile_pic_base64}
                                                            altName={
                                                                review.targetBusiness?.displayName ||
                                                                review.targetBusiness?.full_name ||
                                                                review.targetBusiness?.instagramData?.full_name
                                                            }
                                                            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {review.targetBusiness?.username || review.targetBusinessUsername || review.targetBusiness?.displayName || review.businessName || 'Unknown'}
                                                        </p>

                                                        <div className="flex items-center space-x-2">
                                                            {review.averageRating && (
                                                                <div className="flex items-center space-x-1">
                                                                    <Star className="text-yellow-400" size={12} fill="currentColor" />
                                                                    <span className="text-xs font-medium">{(review.averageRating ?? 0).toFixed(1)}</span>
                                                                </div>
                                                            )}
                                                            {review.reviewCount && (
                                                                <span className="text-xs text-gray-500">(📝 {review.reviewCount || 0})</span>
                                                            )}
                                                            {(review.targetBusiness?.instagramData?.followers != null || review.targetBusiness?.followers != null) && (
                                                                <span className="text-xs text-gray-500">
                                                                    <Users size={10} className="inline-block align-middle" /> {formatFollowers(review.targetBusiness?.instagramData?.followers ?? review.targetBusiness?.followers ?? 0)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => {
                                                setActiveFilters(prev => ({ ...prev, category: category.id }));
                                                document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
                                            }}
                                            className="w-full mt-4 text-purple-600 text-sm font-medium hover:text-purple-700 transition-colors"
                                        >
                                            View All {category.name} →
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Top Rated by categories - Filter */}
            {activeFilters.category != 'all' && (

                <section className="py-6 sm:py-8 bg-gradient-to-r from-purple-50 to-pink-50">

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                {activeFilters.category !== 'all'
                                    ? `🏆 Top Rated by ${categories.find(c => c.id === activeFilters.category)?.name}`
                                    : '🏆 Top Rated by Category'
                                }
                            </h2>
                            <p className="text-gray-600 px-4 sm:px-0">Discover the highest-rated service providers in each category</p>
                        </div>
                        {/* <pre><code>{JSON.stringify(categories.filter(c => c.id === activeFilters.category), null, 2)}</code></pre> */}


                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                            {categories.filter(c => c.id === activeFilters.category).map(category => {

                                // <pre><code>{JSON.stringify(category, null, 2)}</code></pre>

                                const reviews = topCategoryReviews[category.id]?.businesses || [];
                                if (reviews.length === 0) return null;
                                return (
                                    <div key={category.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                                                {category.icon}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900">
                                                    <span className="ml-2">{category.name}</span>
                                                    <Star className="text-yellow-400 inline-block mr-1" size={12} fill="currentColor" />
                                                    {(topCategoryReviews[category.id]?.totalAverageRating ?? 0).toFixed(1)}
                                                    {topCategoryReviews[category.id]?.totalReviewCount && (
                                                        <span className="text-xs text-gray-500 ml-1">(📝 {topCategoryReviews[category.id]?.totalReviewCount || 0})</span>
                                                    )}

                                                </h3>
                                                <p className="text-sm text-gray-600">{category.description}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            {reviews.map((review, index) => (
                                                <div
                                                    key={review.id || review._id}
                                                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                                    onClick={() => openServiceProfile(review.targetBusiness, review.reviews)}
                                                >
                                                    <div className="flex items-center justify-center w-6 h-6">
                                                        {index === 0 && <span className="text-yellow-500">🥇</span>}
                                                        {index === 1 && <span className="text-gray-400">🥈</span>}
                                                        {index === 2 && <span className="text-orange-600">🥉</span>}
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <ProfileImage
                                                            profilePicture={
                                                                review.targetBusiness?.profilePicture ||
                                                                review.targetBusiness?.profile_pic_url ||
                                                                review.targetBusiness?.instagramData?.profile_pic_url
                                                            }
                                                            profile_pic_base64={review.targetBusiness?.profile_pic_base64}
                                                            altName={
                                                                review.targetBusiness?.displayName ||
                                                                review.targetBusiness?.full_name ||
                                                                review.targetBusiness?.instagramData?.full_name
                                                            }
                                                            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {review.targetBusiness?.username || review.targetBusinessUsername || review.targetBusiness?.displayName || review.businessName || 'Unknown'}
                                                        </p>

                                                        <div className="flex items-center space-x-2">
                                                            {review.averageRating && (
                                                                <div className="flex items-center space-x-1">
                                                                    <Star className="text-yellow-400" size={12} fill="currentColor" />
                                                                    <span className="text-xs font-medium">{(review.averageRating ?? 0).toFixed(1)}</span>
                                                                </div>
                                                            )}
                                                            {review.reviewCount && (
                                                                <span className="text-xs text-gray-500">(📝 {review.reviewCount || 0})</span>
                                                            )}
                                                            {(review.targetBusiness?.instagramData?.followers != null || review.targetBusiness?.followers != null) && (
                                                                <span className="text-xs text-gray-500">
                                                                    <Users size={10} className="inline-block align-middle" /> {formatFollowers(review.targetBusiness?.instagramData?.followers ?? review.targetBusiness?.followers ?? 0)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => {
                                                setActiveFilters(prev => ({ ...prev, category: category.id }));
                                                document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
                                            }}
                                            className="w-full mt-4 text-purple-600 text-sm font-medium hover:text-purple-700 transition-colors"
                                        >
                                            View All {category.name} →
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )
            }

            {/* Top Rateed by categories */}
            {
                activeFilters.category === 'all' && activeFilters.region === 'all' && !searchQuery && (
                    <section className="py-8 bg-gradient-to-r from-purple-50 to-pink-50">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                    {activeFilters.category !== 'all'
                                        ? `🏆 Top Rated by ${categories.find(c => c.id === activeFilters.category)?.name}`
                                        : '🏆 Top Rated by Category'
                                    }
                                </h2>
                                {/* <h2 className="text-3xl font-bold text-gray-900 mb-2">🏆 Top Rated by Category</h2> */}
                                <p className="text-gray-600">Discover the highest-rated service providers in each category</p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                                {categories.map(category => {
                                    const reviews = topCategoryReviews[category.id]?.businesses || [];
                                    if (reviews.length === 0) return null;
                                    return (
                                        <div key={category.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                                                    {category.icon}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">
                                                        <span className="ml-2">{category.name}</span>
                                                        <Star className="text-yellow-400 inline-block mr-1" size={12} fill="currentColor" />
                                                        {(topCategoryReviews[category.id]?.totalAverageRating ?? 0).toFixed(1)}
                                                        {topCategoryReviews[category.id]?.totalReviewCount && (
                                                            <span className="text-xs text-gray-500 ml-1">(📝 {topCategoryReviews[category.id]?.totalReviewCount || 0})</span>
                                                        )}

                                                    </h3>
                                                    <p className="text-sm text-gray-600">{category.description}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {reviews.map((review, index) => (
                                                    <div
                                                        key={review.id || review._id}
                                                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                                        onClick={() => openServiceProfile(review.targetBusiness, review.reviews)}
                                                    >
                                                        <div className="flex items-center justify-center w-6 h-6">
                                                            {index === 0 && <span className="text-yellow-500">🥇</span>}
                                                            {index === 1 && <span className="text-gray-400">🥈</span>}
                                                            {index === 2 && <span className="text-orange-600">🥉</span>}
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <ProfileImage
                                                                profilePicture={
                                                                    review.targetBusiness?.profilePicture ||
                                                                    review.targetBusiness?.profile_pic_url ||
                                                                    review.targetBusiness?.instagramData?.profile_pic_url
                                                                }
                                                                profile_pic_base64={review.targetBusiness?.profile_pic_base64}
                                                                altName={
                                                                    review.targetBusiness?.displayName ||
                                                                    review.targetBusiness?.full_name ||
                                                                    review.targetBusiness?.instagramData?.full_name
                                                                }
                                                                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                {review.targetBusiness?.username || review.targetBusinessUsername || review.targetBusiness?.displayName || review.businessName || 'Unknown'}
                                                            </p>

                                                            <div className="flex items-center space-x-2">
                                                                {review.averageRating && (
                                                                    <div className="flex items-center space-x-1">
                                                                        <Star className="text-yellow-400" size={12} fill="currentColor" />
                                                                        <span className="text-xs font-medium">{(review.averageRating ?? 0).toFixed(1)}</span>
                                                                    </div>
                                                                )}
                                                                {review.reviewCount && (
                                                                    <span className="text-xs text-gray-500">(📝 {review.reviewCount || 0})</span>
                                                                )}
                                                                {(review.targetBusiness?.instagramData?.followers != null || review.targetBusiness?.followers != null) && (
                                                                    <span className="text-xs text-gray-500">
                                                                        <Users size={10} className="inline-block align-middle" /> {formatFollowers(review.targetBusiness?.instagramData?.followers ?? review.targetBusiness?.followers ?? 0)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setActiveFilters(prev => ({ ...prev, category: category.id }));
                                                    document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
                                                }}
                                                className="w-full mt-4 text-purple-600 text-sm font-medium hover:text-purple-700 transition-colors"
                                            >
                                                View All {category.name} →
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )
            }




            {/* Services Grid - Filter*/}
            {activeFilters.category != 'all' &&
                <section id="services-section" className="py-8">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Section Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {activeFilters.category !== 'all'
                                        ? `${categories.find(c => c.id === activeFilters.category)?.name} Services`
                                        : 'All Services'
                                    }
                                </h2>
                                <p className="text-gray-600 mt-1">
                                    {activeFilters.category !== 'all'
                                        ? `Top-rated ${categories.find(c => c.id === activeFilters.category)?.name.toLowerCase()} providers`
                                        : 'Discover and compare service providers'
                                    }
                                </p>
                            </div>

                            {filteredServices.length > 0 && (
                                <div className="flex items-center space-x-4">
                                    <div className="text-sm text-gray-500">
                                        Sorted by: <span className="font-medium text-gray-900">Highest Rated</span>
                                    </div>
                                </div>
                            )}
                        </div>



                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.isArray(topCategoryReviews[activeFilters.category]?.businesses || []) && topCategoryReviews[activeFilters.category]?.businesses.map(service => (
                                    <ServiceCard
                                        key={service.id}
                                        service={service.targetBusiness || service.targetBusiness?.instagramData}
                                        reviews={service.reviews}
                                        reviewCount={service.reviewCount}
                                        averageRating={service.averageRating}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {Array.isArray(topCategoryReviews[activeFilters.category]?.businesses || []) && topCategoryReviews[activeFilters.category]?.businesses.map(service => (
                                    <ServiceListItem
                                        key={service.id}
                                        service={service.targetBusiness || service.targetBusiness?.instagramData}
                                        reviews={service.reviews}
                                        reviewCount={service.reviewCount}
                                        averageRating={service.averageRating}
                                    />
                                ))}
                            </div>
                        )}

                        {filteredServices.length === 0 && (
                            <div className="text-center py-16">
                                {topCategoryReviews[activeFilters.category]?.businesses.length === 0 && (
                                    <>
                                        <div className="text-6xl mb-4">🔍</div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                                        <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p>
                                    </>
                                )}

                                {activeFilters.category != 'all' && (
                                    <button
                                        onClick={() => {
                                            setActiveFilters({
                                                category: 'all',
                                                region: 'all',
                                                reviewType: 'all',
                                                priceRange: 'all',
                                                rating: 'all',
                                                verified: false,
                                                availability: 'all'
                                            });
                                            setSearchQuery('');
                                            setSearchSuggestions([]);
                                            setSearchResult([]);
                                        }}
                                        className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                    >
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            }

            {/* ----------------------------------------- */}

            {/* Services Grid */}
            <section id="services-section" className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">
                                All Services
                                {/* {activeFilters.category !== 'all'
                                    ? `${categories.find(c => c.id === activeFilters.category)?.name} Services`
                                    : 'All Services'
                                } */}
                            </h2>
                            <p className="text-gray-600 mt-1">
                                {activeFilters.category !== 'all'
                                    ? `Top-rated ${categories.find(c => c.id === activeFilters.category)?.name.toLowerCase()} providers`
                                    : 'Discover and compare service providers'
                                }
                            </p>
                        </div>

                        {filteredServices.length > 0 && (
                            <div className="flex items-center space-x-4">
                                <div className="text-sm text-gray-500">
                                    Sorted by: <span className="font-medium text-gray-900">Highest Rated</span>
                                </div>
                            </div>
                        )}
                    </div>



                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.isArray(businesses) && businesses.map(service => (
                                <ServiceCard
                                    key={service.id}
                                    service={service.searchData}
                                    reviews={service.reviews}
                                    reviewCount={service.reviewCount}
                                    averageRating={service.averageRating}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {Array.isArray(businesses) && businesses.map(service => (
                                <ServiceListItem
                                    key={service.id}
                                    service={service.searchData}
                                    reviews={service.reviews}
                                    reviewCount={service.reviewCount}
                                    averageRating={service.averageRating}
                                />
                            ))}
                        </div>
                    )}

                    {/* {filteredServices.length === 0 && (
                        <div className="text-center py-16">
                            <div className="text-6xl mb-4">🔍</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                            <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p>
                            <button
                                onClick={() => {
                                    setActiveFilters({
                                        category: 'all',
                                        region: 'all',
                                        reviewType: 'all',
                                        priceRange: 'all',
                                        rating: 'all',
                                        verified: false,
                                        availability: 'all'
                                    });
                                    setSearchQuery('');
                                    setSearchSuggestions([]);
                                    setSearchResult([]);
                                }}
                                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )} */}
                </div>
            </section>

            {/* Services Grid - Top Rated - Business or Professional */}
            <section id="services-section" className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">
                                All Services - Business or Professional
                                {/* {activeFilters.category !== 'all'
                                    ? `${categories.find(c => c.id === activeFilters.category)?.name} Services`
                                    : 'All Services - Business or Professional'
                                } */}
                            </h2>
                            <p className="text-gray-600 mt-1">
                                {activeFilters.category !== 'all'
                                    ? `Top-rated ${categories.find(c => c.id === activeFilters.category)?.name.toLowerCase()} providers`
                                    : 'Discover and compare service providers'
                                }
                            </p>
                        </div>

                        {filteredServices.length > 0 && (
                            <div className="flex items-center space-x-4">
                                <div className="text-sm text-gray-500">
                                    Sorted by: <span className="font-medium text-gray-900">Highest Rated</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {topBusinesses && (
                        viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.isArray(topBusinesses) && topBusinesses.map(service => (
                                    <ServiceCard
                                        key={service.id}
                                        service={service.searchData}
                                        reviews={service.reviews}
                                        reviewCount={service.reviewCount}
                                        averageRating={service.averageRating}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {Array.isArray(topBusinesses) && topBusinesses.map(service => (
                                    <ServiceListItem
                                        key={service.id}
                                        service={service.searchData}
                                        reviews={service.reviews}
                                        reviewCount={service.reviewCount}
                                        averageRating={service.averageRating}
                                    />
                                ))}
                            </div>
                        )
                    )}

                    {/* {
                        filteredServices.length === 0 && (
                            <div className="text-center py-16">
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                                <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p>
                                <button
                                    onClick={() => {
                                        setActiveFilters({
                                            category: 'all',
                                            region: 'all',
                                            reviewType: 'all',
                                            priceRange: 'all',
                                            rating: 'all',
                                            verified: false,
                                            availability: 'all'
                                        });
                                        setSearchQuery('');
                                        setSearchSuggestions([]);
                                        setSearchResult([]);
                                    }}
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        )
                    } */}
                </div>
            </section>



            {/* User Reviews Section */}
            {(isLoggedIn || isVerified) && (
                <section className="py-8 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">📝 Your Reviews</h2>
                            <p className="text-gray-600">
                                {userReviews && userReviews.length > 0
                                    ? `Your contributions to the community • ${userReviews.length} review${userReviews.length !== 1 ? 's' : ''}`
                                    : 'Start sharing your experiences with the community'
                                }
                            </p>
                        </div>

                        {userReviews && userReviews.length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {userReviews.slice(0, userReviewsToShow).map((review, index) => {

                                        // Check each field for objects
                                        Object.keys(review).forEach(key => {
                                            const value = review[key];
                                            // if (typeof value === 'object' && value !== null) {
                                            //   console.log(`🚨 Object field found: ${key}`, value);
                                            // }
                                        });

                                        return (

                                            <div key={review.reviewId || review.id || review._id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">

                                                {/* Header with Business Info */}
                                                < div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
                                                    <div className="flex items-center justify-between">

                                                        <div>
                                                            <h3 className="font-bold text-lg truncate">
                                                                {(() => {
                                                                    const businessUsername = review.targetBusiness?.username ||
                                                                        review.targetBusinessUsername ||
                                                                        review.business;

                                                                    const businessFullname = review.targetBusiness?.full_name

                                                                    // If business name is 'unknown' or empty, try to extract from other fields
                                                                    if (!businessUsername || businessUsername === 'unknown') {
                                                                        // Try to get from reviewer identifier as fallback
                                                                        const reviewerIdentifier = review.reviewer?.identifier ||
                                                                            review.reviewer?.legacyIdentifier;

                                                                        if (reviewerIdentifier && reviewerIdentifier !== 'anonymous' && !reviewerIdentifier.startsWith('anon_')) {
                                                                            // Clean the identifier and add single @
                                                                            const cleanIdentifier = reviewerIdentifier.replace(/^@+/, '');
                                                                            return `@${cleanIdentifier}`;
                                                                        }

                                                                        return 'Business Review';
                                                                    }

                                                                    // Clean the username and add single @
                                                                    const cleanUsername = businessUsername.replace(/^@+/, '');
                                                                    return `${cleanUsername}`;
                                                                })()}
                                                            </h3>
                                                            <span>{review.targetBusiness?.full_name}</span>
                                                            <p className="text-purple-100 text-sm">
                                                                {(() => {
                                                                    const displayName = review.targetBusiness?.displayName ||
                                                                        review.businessName;

                                                                    // Don't show anything if it's unknown, empty, or doesn't exist
                                                                    if (!displayName || displayName === 'unknown' || displayName === 'Instagram Business') {
                                                                        return '';
                                                                    }

                                                                    return String(displayName);
                                                                })()}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-yellow-200 text-sm">
                                                                {(() => {
                                                                    const rating = review.rating;
                                                                    const ratingValue = typeof rating === 'object' && rating?.overall ? rating.overall : (rating || 0);
                                                                    return '★'.repeat(ratingValue) + '☆'.repeat(5 - ratingValue);
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Review Content */}
                                                <div className="p-4">
                                                    {/* Service Type & Category */}
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                                                            {(() => {
                                                                const serviceType = review.serviceType || review.serviceDetails?.serviceType;
                                                                if (typeof serviceType === 'object' && serviceType?.name) {
                                                                    return String(serviceType.name);
                                                                }
                                                                return String(serviceType || 'General Service');
                                                            })()}
                                                        </div>
                                                        {review.reviewType && (
                                                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                                                                {(() => {
                                                                    const reviewType = review.reviewType;
                                                                    if (typeof reviewType === 'object' && reviewType?.name) {
                                                                        return String(reviewType.name);
                                                                    }
                                                                    return String(reviewType);
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Review Text */}
                                                    <div className="mb-4">
                                                        <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">
                                                            {(() => {
                                                                const comment = review.comment || review.content?.comment || review.text;
                                                                return String(comment || 'No comment provided');
                                                            })()}
                                                        </p>
                                                    </div>

                                                    {/* Footer with Details */}
                                                    <div className="border-t pt-3 space-y-2">
                                                        {/* Verification Status */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-2">
                                                                {review.verification?.isVerified || review.verified ? (
                                                                    <>
                                                                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                                                            <span className="text-white text-xs">✓</span>
                                                                        </div>
                                                                        <span className="text-green-600 text-xs font-medium">Verified Review</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                                                                            <span className="text-white text-xs">?</span>
                                                                        </div>
                                                                        <span className="text-gray-500 text-xs">Anonymous Review</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <span className="text-gray-400 text-xs">
                                                                {review.metadata?.completedAt ? new Date(review.metadata.completedAt).toLocaleDateString() :
                                                                    review.createdAt ? new Date(review.createdAt).toLocaleDateString() :
                                                                        'Recent'}
                                                            </span>
                                                        </div>

                                                        {/* Region & Verification Method */}
                                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                                            <span>
                                                                📍 {(() => {
                                                                    const region = review.region || review.location?.region;
                                                                    if (typeof region === 'object' && region?.name) {
                                                                        return String(region.name);
                                                                    }
                                                                    return String(region || 'Global');
                                                                })()}
                                                            </span>
                                                            {(review.verification?.method || review.verificationMethod) && (
                                                                <span className="capitalize">
                                                                    via {String(review.verification?.method || review.verificationMethod)}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Promotion Code if exists */}
                                                        {review.promotionCode && String(review.promotionCode).trim() && (
                                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-2">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-yellow-600 text-xs">🎟️ Promotion Code:</span>
                                                                    <span className="text-yellow-800 text-xs font-mono font-medium">
                                                                        {(() => {
                                                                            try {
                                                                                const code = review.promotionCode;
                                                                                if (typeof code === 'string') return code;
                                                                                if (typeof code === 'object') return JSON.stringify(code);
                                                                                return String(code);
                                                                            } catch (error) {
                                                                                console.error('Error rendering promotion code:', error);
                                                                                return 'CODE_ERROR';
                                                                            }
                                                                        })()}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {userReviewsToShow < userReviews.length && (
                                    <div className="flex justify-center mt-6">
                                        <button
                                            onClick={() => setUserReviewsToShow(prev => prev + 6)}
                                            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-all"
                                        >
                                            Load More
                                        </button>
                                    </div>
                                )}
                            </>

                        ) : (
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8 text-center max-w-md mx-auto">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MessageCircle className="text-white" size={24} />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Reviews Yet</h3>
                                <p className="text-gray-600 mb-6">You haven't written any reviews yet. Start sharing your experiences with the IGReview community!</p>
                                <button
                                    onClick={() => setShowQuickReview(true)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all"
                                >
                                    <PlusCircle size={20} />
                                    Write Your First Review
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )
            }
        </div >
    );
}


export default HomePage;
